import {
  Breadcrumb,
  Alert,
  Button,
  ButtonGroup,
  ButtonGroupItem,
  Form,
} from '@boozallen-uip/uswds-react-core';
import { IndeterminateSpinner } from '@boozallen-uip/uswds-react-loading';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { flushSync } from 'react-dom';
import { Link, useBlocker, useNavigate, useParams } from 'react-router-dom';
import useProduct from '../../hooks/useProduct.hook';
import { SectionContainer } from '../shared.styled';
import useHyperlink from '../../hooks/useHyperlink.hook';
import useReport from '../../hooks/useReport.hook';
import inSiteAcrAddedOn from './utils/inSiteAcrAddedOn';
import {
  HOME_URL,
  ACCESS_DENIED,
  ACCESS_LEVEL_ADMIN,
  ACCESS_LEVEL_USER,
  ACCESS_NO_LOGIN,
  BASE_TITLE,
  PRODUCT_LIST_SUBMIT_URL,
  REQUEST_LINK_MESSAGE,
  stateLabelValues,
  PRODUCT_SUBMIT_URL,
  REACT_APP_ACR_API_BASE_URL,
  REACT_APP_ACR_API_PROTOCOL,
  CONTACT_URL,
  PRODUCT_VISIBILITY_PUBLIC,
  PRODUCT_VISIBILITY_BUYERS_ONLY,
  PRODUCT_VISIBILITY_HIDE,
  ENTITY_TYPE_VENDOR,
  ENTITY_TYPE_AGENCY
} from '../../utlities/core.constants';
import { UserContext } from '../UserContext';
import useCurrentOrg from '../../hooks/useCurrentOrg.hook';
import AccessStatus from '../../components/AccessStatus.component';
import formatDate from '../../utlities/date.utility';
import { isValidAbsoluteUrl } from '../../components/acr-editor/utils/reportValidation';
import ProductInformationSection from './components/ProductInformationSection';
import ProductVisibilitySection from './components/ProductVisibilitySection';
import AcrInformationSection from './components/AcrInformationSection';
import ProductPocSection from './components/ProductPocSection';
import { runSaveDraftProductFlow } from './utils/saveDraftProductFlow';
import { runSubmitProductFlow, SUBMIT_GENERIC_ERROR } from './utils/submitProductFlow';
import {
  shouldFailSubmitStep,
  SUBMIT_TEST_FAILURE_STATUS,
} from './utils/submitProductTestMode';
import {
  shouldFailSaveDraftStep,
  SAVE_DRAFT_TEST_FAILURE_STATUS,
} from './utils/saveDraftProductTestMode';
import {
  createEmptyProductPoc,
  formatPhoneNumber,
  normalizeProductPocPayload,
  isValidAuthoredDate,
  validateProductPoc,
} from './utils/productFormValidation';

function FormFieldRequired() {
  return <span className='text-red'>(Required)</span>
}

function renderInlineFieldError(message) {
  if (!message) {
    return null;
  }
  return (
    <div className="mt-1 text-sm text-red" role="alert">
      {message}
    </div>
  );
}

const FORM_ERROR_KEY = "form.general";
const VALIDATION_SUMMARY_HEADING = "Missing Required Information";
const FIELD_FOCUS_IDS = {
  "product.name": "productName",
  "product.category": "category",
  "product.description": "productDescription",
  // RadioGroup never sets this id on a DOM node; the first radio is `${groupId}--0`.
  "product.visibility": "productVisibility--0",
  "hyperlink.url": "hyperlinkUrl",
  "hyperlink.acrType": "acrType",
  "hyperlink.authoredDate": "authoredDate",
  "productPoc.phoneNumber": "productPocPhone",
  "productPoc.email": "productPocEmail",
  "productPoc.zipCode": "productPocZipCode",
};
const FIELD_FOCUS_ORDER = [
  "product.name",
  "product.category",
  "product.description",
  "product.visibility",
  "hyperlink.url",
  "hyperlink.acrType",
  "hyperlink.authoredDate",
  "productPoc.phoneNumber",
  "productPoc.email",
  "productPoc.zipCode",
];

function getValidationSummaryErrors(submitFieldErrors) {
  return FIELD_FOCUS_ORDER
    .filter((field) => submitFieldErrors[field] && FIELD_FOCUS_IDS[field])
    .map((field) => ({ field, id: FIELD_FOCUS_IDS[field], message: submitFieldErrors[field] }));
}

function getHiddenAgencyIds(productResponse) {
  if (Array.isArray(productResponse?.hiddenAgencyIds)) {
    return productResponse.hiddenAgencyIds.filter(Boolean).map((id) => String(id));
  }

  if (Array.isArray(productResponse?.hiddenAgencies)) {
    return productResponse.hiddenAgencies
      .map((agency) => (typeof agency === "string" ? agency : agency?.id))
      .filter(Boolean)
      .map((id) => String(id));
  }

  return [];
}

function normalizeProductPocForForm(p) {
  if (!p) {
    return {
      id: null,
      ...createEmptyProductPoc(),
    };
  }
  return {
    ...p,
    phoneNumber: formatPhoneNumber(p.phoneNumber),
    email: p.email ?? "",
    street1: p.street1 ?? "",
    street2: p.street2 ?? "",
    city: p.city ?? "",
    state: p.state ?? null,
    zipCode: p.zipCode ?? "",
  };
}

/** Product POST/PATCH: omit hidden-agency exclusions unless visibility is Buyers-only. */
function buildProductSavePayload(product) {
  const fedRampStatus = String(product.fedRampStatus || "").trim();

  return {
    ...product,
    productPoc: normalizeProductPocPayload(product?.productPoc),
    fedRampStatus: fedRampStatus || null,
    hiddenAgencyIds:
      product.visibility === PRODUCT_VISIBILITY_BUYERS_ONLY
        ? [...new Set((product.hiddenAgencyIds || []).map((id) => String(id)).filter(Boolean))]
        : [],
  };
}

function createEmptyHyperlinkState() {
  return {
    id: undefined,
    url: "",
    acrType: "",
    requestRequired: false,
    authoredDate: "",
    version: "",
    name: "",
  };
}

function safeFormatDateForInput(value) {
  if (value == null || value === "") return "";
  try {
    const iso = typeof value === "string" ? value : new Date(value).toISOString();
    return formatDate(iso);
  } catch {
    return "";
  }
}

function mapHyperlinkFromApiToForm(h) {
  if (!h) {
    return createEmptyHyperlinkState();
  }
  return {
    ...h,
    url: h.url ?? "",
    acrType: h.acrType ?? "",
    requestRequired: Boolean(h.requestRequired),
    authoredDate: h.authoredDate ? safeFormatDateForInput(h.authoredDate) : "",
    version: h.version ?? "",
    name: h.name ?? "",
  };
}

function normalizeHyperlinkForComparison(hyperlink) {
  const requestRequired = Boolean(hyperlink?.requestRequired);
  return {
    url: String(hyperlink?.url || "").trim(),
    acrType: String(hyperlink?.acrType || "").trim(),
    requestRequired,
    authoredDate: String(hyperlink?.authoredDate || "").trim(),
    version: String(hyperlink?.version || "").trim(),
    name: requestRequired ? "" : String(hyperlink?.name || "").trim(),
  };
}

function hasHyperlinkChanges(currentHyperlink, loadedHyperlink) {
  const current = normalizeHyperlinkForComparison(currentHyperlink);
  const loaded = normalizeHyperlinkForComparison(loadedHyperlink);
  return Object.keys(current).some((key) => current[key] !== loaded[key]);
}

function normalizeVisibilityForForm(visibility, isPrivate) {
  const rawVisibility = String(visibility || "").trim();
  if (rawVisibility) {
    const normalized = rawVisibility.toLowerCase();
    if (normalized === PRODUCT_VISIBILITY_PUBLIC.toLowerCase()) {
      return PRODUCT_VISIBILITY_PUBLIC;
    }
    if (normalized === PRODUCT_VISIBILITY_BUYERS_ONLY.toLowerCase()) {
      return PRODUCT_VISIBILITY_BUYERS_ONLY;
    }
    if (normalized === PRODUCT_VISIBILITY_HIDE.toLowerCase() || normalized === "hidden") {
      return PRODUCT_VISIBILITY_HIDE;
    }
  }

  if (isPrivate === true) {
    return PRODUCT_VISIBILITY_HIDE;
  }
  if (isPrivate === false) {
    return PRODUCT_VISIBILITY_PUBLIC;
  }

  return PRODUCT_VISIBILITY_PUBLIC;
}

function normalizeProductForForm(p) {
  if (!p) {
    return {
      visibility: PRODUCT_VISIBILITY_PUBLIC,
      name: "",
      version: "",
      description: "",
      category: "",
      pscCode: "",
      fedRampStatus: "",
      productPoc: normalizeProductPocForForm(null),
    };
  }
  return {
    ...p,
    name: p.name ?? "",
    version: p.version ?? "",
    description: p.description ?? "",
    category: p.category ?? "",
    pscCode: p.pscCode ?? "",
    fedRampStatus: p.fedRampStatus ?? "",
    visibility: normalizeVisibilityForForm(p.visibility, p.isPrivate),
    productPoc: normalizeProductPocForForm(p.productPoc),
  };
}

/** ISO string for API DateTime (CreateHyperlinkRequest). */
function authoredFormValueToApiIso(yyyyMmDd) {
  if (yyyyMmDd == null || String(yyyyMmDd).trim() === "") {
    return new Date().toISOString();
  }
  const raw = String(yyyyMmDd).trim();

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0));
    return date.toISOString();
  }

  const usMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (usMatch) {
    const [, month, day, year] = usMatch;
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0));
    return date.toISOString();
  }

  return new Date(raw).toISOString();
}

const PRODUCT_NAME_REQUIRED_MESSAGE = "Please include a name for your new product.";
const PRODUCT_CATEGORY_REQUIRED_MESSAGE = "Please select a category for your new product.";
const PRODUCT_DESCRIPTION_REQUIRED_MESSAGE = "Please include a description for your new product.";
const PRODUCT_VISIBILITY_REQUIRED_MESSAGE = "Select a product visibility option.";
const ACR_TYPE_REQUIRED_MESSAGE = "Select an ACR type.";
const ACR_AUTHORED_DATE_REQUIRED_MESSAGE = "Enter the date the ACR was created.";
const ACR_AUTHORED_DATE_INVALID_MESSAGE = "Enter a valid date.";
const ACR_LINK_REQUIRED_MESSAGE = "Provide an ACR link or select \"Request link from product POC\".";
const ACR_LINK_INVALID_MESSAGE = "Enter a valid URL (for example: https://www.example.com).";

function hasAnyHyperlinkInput(hyperlink) {
  return Boolean(
    hyperlink?.requestRequired
    || String(hyperlink?.url || "").trim()
    || String(hyperlink?.acrType || "").trim()
    || String(hyperlink?.authoredDate || "").trim()
    || String(hyperlink?.version || "").trim()
    || String(hyperlink?.name || "").trim()
  );
}

function normalizeHyperlinkUrlForSave(url) {
  const trimmed = String(url || "").trim();
  if (!trimmed) {
    return "";
  }
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function buildHyperlinkPayloadForApi(hyperlink) {
  return {
    ...hyperlink,
    url: normalizeHyperlinkUrlForSave(hyperlink?.url),
    name: hyperlink?.requestRequired ? null : String(hyperlink?.name || "").trim(),
    authoredDate: authoredFormValueToApiIso(hyperlink?.authoredDate),
    isActive: true,
  };
}

function validateDraftHyperlink(hyperlink) {
  if (!hasAnyHyperlinkInput(hyperlink)) {
    return null;
  }

  if (!String(hyperlink?.acrType || "").trim()) {
    return { field: "hyperlink.acrType", message: ACR_TYPE_REQUIRED_MESSAGE };
  }
  if (!String(hyperlink?.authoredDate || "").trim()) {
    return { field: "hyperlink.authoredDate", message: ACR_AUTHORED_DATE_REQUIRED_MESSAGE };
  }
  if (!isValidAuthoredDate(hyperlink?.authoredDate)) {
    return { field: "hyperlink.authoredDate", message: ACR_AUTHORED_DATE_INVALID_MESSAGE };
  }

  if (!hyperlink?.requestRequired) {
    const hyperlinkUrl = normalizeHyperlinkUrlForSave(hyperlink?.url);
    if (!hyperlinkUrl) {
      return { field: "hyperlink.url", message: ACR_LINK_REQUIRED_MESSAGE };
    }
    if (!isValidAbsoluteUrl(hyperlinkUrl)) {
      return { field: "hyperlink.url", message: ACR_LINK_INVALID_MESSAGE };
    }
  }

  return null;
}

function validateSubmitPayload(product, hyperlink) {
  const failures = [];
  const addFailure = (field, message) => {
    failures.push({ field, message });
  };

  if (!product.name?.trim()) addFailure("product.name", PRODUCT_NAME_REQUIRED_MESSAGE);
  if (!String(product.category || "").trim()) addFailure("product.category", PRODUCT_CATEGORY_REQUIRED_MESSAGE);
  if (!String(product.description || "").trim()) addFailure("product.description", PRODUCT_DESCRIPTION_REQUIRED_MESSAGE);
  if (!product.visibility) addFailure("product.visibility", PRODUCT_VISIBILITY_REQUIRED_MESSAGE);
  if (!String(hyperlink.acrType || "").trim()) addFailure("hyperlink.acrType", ACR_TYPE_REQUIRED_MESSAGE);
  if (!String(hyperlink.authoredDate || "").trim()) addFailure("hyperlink.authoredDate", ACR_AUTHORED_DATE_REQUIRED_MESSAGE);
  else if (!isValidAuthoredDate(hyperlink.authoredDate)) addFailure("hyperlink.authoredDate", ACR_AUTHORED_DATE_INVALID_MESSAGE);
  const hyperlinkUrl = normalizeHyperlinkUrlForSave(hyperlink.url);
  if (!hyperlink.requestRequired) {
    if (!hyperlinkUrl) {
      addFailure("hyperlink.url", ACR_LINK_REQUIRED_MESSAGE);
    } else if (!isValidAbsoluteUrl(hyperlinkUrl)) {
      addFailure("hyperlink.url", ACR_LINK_INVALID_MESSAGE);
    }
  }
  validateProductPoc(product?.productPoc).forEach(({ field, message }) => addFailure(field, message));

  return {
    isValid: failures.length === 0,
    failures,
  };
}

export default function ProductSubmit() {
  const unsavedChangesPrompt =
    "Leaving this page will discard any unsaved changes. Are you sure you want to leave?";
  const [banner, setBanner] = useState(null);
  const bannerRef = useRef(null);
  const summaryHeadingRef = useRef(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [skipUnsavedPrompt, setSkipUnsavedPrompt] = useState(false);
  const isHandlingBlockerRef = useRef(false);
  const isHydratingRef = useRef(false);
  const hasInitializedDirtyTrackingRef = useRef(false);
  const loadedHyperlinkRef = useRef(createEmptyHyperlinkState());
  const [product, setProduct] = useState({
    visibility: PRODUCT_VISIBILITY_PUBLIC,
    hiddenAgencyIds: [],
    name: "",
    version: "",
    description: "",
    category: "",
    pscCode: "",
    fedRampStatus: "",
  });
  const [agencyLoadError, setAgencyLoadError] = useState(null);
  const [, setHyperlinkName] = useState("");
  const [hyperlink, setHyperlink] = useState(() => createEmptyHyperlinkState());
  const [categories, setCategories] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [acrTypes, setAcrTypes] = useState([]);
  const [fedRampStatuses, setFedRampStatuses] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [submitFieldErrors, setSubmitFieldErrors] = useState({});
  const navigate = useNavigate();
  const { submitProduct, saveDraftProduct, fetchCategories, fetchFedRampStatuses, fetchFilteredProductAll, pscCodes, getProduct, updateProduct } = useProduct();
  const { submitHyperlink, getHyperlinkByProductId, getHyperlinkById, fetchAcrTypes, updateHyperlink } = useHyperlink();
  const { fetchReportsByProduct } = useReport();
  const { productId } = useParams();
  const [workingProductId, setWorkingProductId] = useState(productId || null);
  // Gates rendering of the form until an existing product's data has been fetched, so fields
  // backed by uncontrolled inputs (e.g. the visibility RadioGroup) never mount with placeholder
  // defaults that they'd fail to visually update away from once the real data arrives.
  const [isLoadingProduct, setIsLoadingProduct] = useState(Boolean(productId));

  const { checkEntityAccessLevel, userEntity, isLoggedIn } = useContext(UserContext);
  const currentOrg = useCurrentOrg();
  const blocker = useBlocker(hasUnsavedChanges && !skipUnsavedPrompt);
  const normalizedProductVisibility = normalizeVisibilityForForm(product.visibility, product.isPrivate);
  const agencyExclusionsApply = normalizedProductVisibility === PRODUCT_VISIBILITY_BUYERS_ONLY;
  const canSaveDraft = !workingProductId || product?.isPublished !== true;
  const selectedHiddenAgencies = useMemo(() => {
    const hiddenIds = product.hiddenAgencyIds || [];
    if (!hiddenIds.length) {
      return [];
    }

    const agenciesById = new Map(agencies.map((agency) => [String(agency.id), agency.name]));
    return hiddenIds.map((id) => ({
      id: String(id),
      name: agenciesById.get(String(id)) || null,
    }));
  }, [agencies, product.hiddenAgencyIds]);

  const hiddenAgencySelectionKey = useMemo(
    () => [...(product.hiddenAgencyIds || [])].map(String).sort().join(','),
    [product.hiddenAgencyIds],
  );
  const prevHiddenAgencySelectionKey = useRef(null);
  const [hiddenAgencySelectionAnnouncement, setHiddenAgencySelectionAnnouncement] = useState('');

  const getFieldError = useCallback((field) => submitFieldErrors[field], [submitFieldErrors]);
  const focusFieldForError = useCallback((field) => {
    const targetId = FIELD_FOCUS_IDS[field];
    if (!targetId) {
      return;
    }
    const element = document.getElementById(targetId);
    if (element && typeof element.focus === "function") {
      element.focus();
    }
  }, []);
  const focusFirstErrorField = useCallback((errorMap) => {
    const firstErrorField = FIELD_FOCUS_ORDER.find((field) => Boolean(errorMap[field]));
    if (firstErrorField) {
      focusFieldForError(firstErrorField);
      return;
    }
    if (errorMap[FORM_ERROR_KEY]) {
      focusFieldForError("product.name");
    }
  }, [focusFieldForError]);
  const inferFieldFromMessage = useCallback((message, fallbackField = FORM_ERROR_KEY) => {
    const text = String(message || "").toLowerCase();
    if (text.includes("product name") || text.includes("already exists")) return "product.name";
    if (text.includes("category")) return "product.category";
    if (text.includes("description")) return "product.description";
    if (text.includes("visibility") || text.includes("hidden agenc")) return "product.visibility";
    if (text.includes("acr type")) return "hyperlink.acrType";
    if (text.includes("acr link") || text.includes("hyperlink") || text.includes(" url")) return "hyperlink.url";
    if (text.includes("date the acr") || text.includes("authored")) return "hyperlink.authoredDate";
    if (text.includes("phone")) return "productPoc.phoneNumber";
    if (text.includes("email")) return "productPoc.email";
    if (text.includes("zip")) return "productPoc.zipCode";
    return fallbackField;
  }, []);
  const setInlineError = useCallback((message, fallbackField = FORM_ERROR_KEY) => {
    const field = inferFieldFromMessage(message, fallbackField);
    const nextErrors = { [field]: message };
    setSubmitFieldErrors(nextErrors);
    focusFirstErrorField(nextErrors);
  }, [focusFirstErrorField, inferFieldFromMessage]);
  const clearFieldErrors = useCallback((fields) => {
    setSubmitFieldErrors((prev) => {
      const next = { ...prev };
      let changed = false;
      const fieldsToClear = new Set([...(fields || []), FORM_ERROR_KEY]);
      fieldsToClear.forEach((field) => {
        if (next[field]) {
          delete next[field];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  const mapFieldFailures = useCallback((failures) => (
    failures.reduce((acc, failure) => {
      if (!acc[failure.field]) {
        acc[failure.field] = failure.message;
      }
      return acc;
    }, {})
  ), []);

  const handleProductNameBlur = useCallback(async () => {
    const rawName = String(product?.name || "").trim();
    if (!rawName || !userEntity?.entity?.id) {
      return;
    }
    try {
      // userEntity.entity.id here is always the logged-in user's own vendor, so include
      // private/draft listings via myListings=true - the org should see everything it owns.
      const result = await fetchFilteredProductAll(rawName, "", "", userEntity.entity.id, true);
      const list = Array.isArray(result) ? result : result?.content ?? [];
      const normalizedName = rawName.toLowerCase();
      const duplicateProduct = list.find((candidate) => {
        const candidateName = String(candidate?.name || "").trim().toLowerCase();
        if (candidateName !== normalizedName) return false;
        const candidateId = String(candidate?.id ?? candidate?.Id ?? "");
        const currentId = String(workingProductId ?? "");
        if (!candidateId) return false;
        if (!currentId) return true;
        return candidateId !== currentId;
      });
      setSubmitFieldErrors((prev) => {
        if (duplicateProduct) {
          return {
            ...prev,
            "product.name": "A product with this name already exists within your organization.",
          };
        }
        if (!prev["product.name"]) {
          return prev;
        }
        if (!String(prev["product.name"]).toLowerCase().includes("already exists")) {
          return prev;
        }
        const next = { ...prev };
        delete next["product.name"];
        return next;
      });
    } catch {
      // Ignore blur validation fetch failures and preserve current form state.
    }
  }, [product?.name, userEntity?.entity?.id, fetchFilteredProductAll, workingProductId]);

  const navigateWithoutUnsavedPrompt = useCallback((destination, state) => {
    flushSync(() => {
      setSkipUnsavedPrompt(true);
      setHasUnsavedChanges(false);
    });
    navigate(destination, state ? { state } : undefined);
  }, [navigate]);

  const createHyperlinkForProduct = useCallback(async (targetProductId, hyperlinkPayload) => {
    const newHyperlinkPayload = { ...hyperlinkPayload };
    delete newHyperlinkPayload.id;

    const hyperlinkResponse = await submitHyperlink({
      ...newHyperlinkPayload,
      productId: targetProductId,
    });

    if (hyperlinkResponse.status >= 300) {
      return { ok: false, reason: "acr-link-upload-failed" };
    }

    const createdHyperlinkId = hyperlinkResponse?.data?.id;
    if (createdHyperlinkId) {
      setHyperlink((prev) => ({ ...prev, id: createdHyperlinkId }));
      loadedHyperlinkRef.current = { ...hyperlink, id: createdHyperlinkId };
    } else {
      loadedHyperlinkRef.current = { ...hyperlink };
    }

    return { ok: true };
  }, [hyperlink, submitHyperlink]);

  const updatePublishedProduct = useCallback(async (targetProductId, productToSave) => {
    // ownerPayload sets only the current org's owner id and nulls the other, so the API never
    // sees this as an attempt to reassign the product to a different organization.
    const updateProductResponse = await updateProduct(targetProductId, {
      ...productToSave,
      id: targetProductId,
      ...currentOrg.ownerPayload,
    });

    if (updateProductResponse.status === 403) {
      return { ok: false, reason: "forbidden" };
    }

    if (updateProductResponse.status >= 300) {
      return { ok: false, reason: "product-update-failed" };
    }

    return { ok: true };
  }, [updateProduct, currentOrg]);

  const submitProductWithTest = useCallback(async (payload) => {
    if (shouldFailSubmitStep("initialProductSubmit")) {
      return { status: SUBMIT_TEST_FAILURE_STATUS };
    }
    return submitProduct(payload);
  }, [submitProduct]);

  const updateHyperlinkWithTest = useCallback(async (...args) => {
    if (shouldFailSubmitStep("hyperlinkSubmit")) {
      return { status: SUBMIT_TEST_FAILURE_STATUS };
    }
    return updateHyperlink(...args);
  }, [updateHyperlink]);

  const submitFlowCreateHyperlinkForProduct = useCallback(async (targetProductId, hyperlinkPayload) => {
    if (shouldFailSubmitStep("hyperlinkSubmit")) {
      return { ok: false, reason: "acr-link-upload-failed" };
    }
    return createHyperlinkForProduct(targetProductId, hyperlinkPayload);
  }, [createHyperlinkForProduct]);

  const updatePublishedProductWithTest = useCallback(async (...args) => {
    if (shouldFailSubmitStep("finalProductSubmit")) {
      return { ok: false, reason: "product-update-failed" };
    }
    return updatePublishedProduct(...args);
  }, [updatePublishedProduct]);

  const saveDraftProductWithTest = useCallback(async (...args) => {
    if (shouldFailSaveDraftStep("initialDraftSave")) {
      return { status: SAVE_DRAFT_TEST_FAILURE_STATUS };
    }
    return saveDraftProduct(...args);
  }, [saveDraftProduct]);

  const updateProductForDraftWithTest = useCallback(async (...args) => {
    if (shouldFailSaveDraftStep("initialDraftSave")) {
      return { status: SAVE_DRAFT_TEST_FAILURE_STATUS };
    }
    return updateProduct(...args);
  }, [updateProduct]);

  const updateHyperlinkForDraftWithTest = useCallback(async (...args) => {
    if (shouldFailSaveDraftStep("hyperlinkSave")) {
      return { status: SAVE_DRAFT_TEST_FAILURE_STATUS };
    }
    return updateHyperlink(...args);
  }, [updateHyperlink]);

  const createHyperlinkForDraftWithTest = useCallback(async (...args) => {
    if (shouldFailSaveDraftStep("hyperlinkSave")) {
      return { ok: false, reason: "acr-link-upload-failed" };
    }
    return createHyperlinkForProduct(...args);
  }, [createHyperlinkForProduct]);

  useEffect(() => {
    setWorkingProductId(productId || null);
  }, [productId]);

  useEffect(() => {
    if (prevHiddenAgencySelectionKey.current === null) {
      prevHiddenAgencySelectionKey.current = hiddenAgencySelectionKey;
      return;
    }
    if (prevHiddenAgencySelectionKey.current === hiddenAgencySelectionKey) {
      return;
    }
    prevHiddenAgencySelectionKey.current = hiddenAgencySelectionKey;
    const n = selectedHiddenAgencies.length;
    if (n === 0) {
      setHiddenAgencySelectionAnnouncement('No agencies selected to hide this listing from.');
    } else {
      setHiddenAgencySelectionAnnouncement(
        `${n} agenc${n === 1 ? 'y' : 'ies'} selected to hide this listing from.`,
      );
    }
  }, [hiddenAgencySelectionKey, selectedHiddenAgencies.length]);

  document.title = banner?.kind === "validation-summary"
    ? `Error: ${BASE_TITLE} - Product Submit`
    : `${BASE_TITLE} - Product Submit`;

  const onSubmitHandler = useCallback(async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setSubmitFieldErrors({});

    try {
      setBanner(null);
      setIsSaving(true);
      const result = await runSubmitProductFlow({
        workingProductId,
        product,
        hyperlink,
        loadedHyperlink: loadedHyperlinkRef.current,
        validateSubmitPayload,
        mapFieldFailures,
        buildProductSavePayload,
        buildHyperlinkPayloadForApi,
        hasHyperlinkChanges,
        updateHyperlink: updateHyperlinkWithTest,
        createHyperlinkForProduct: submitFlowCreateHyperlinkForProduct,
        updatePublishedProduct: updatePublishedProductWithTest,
        submitProduct: submitProductWithTest,
        ownerPayload: currentOrg.ownerPayload,
      });

      if (!result.ok) {
        if (result.type === "validation_errors") {
          setSubmitFieldErrors(result.errors);
          setBanner({ kind: "validation-summary" });
          return;
        }
        if (result.type === "inline_error") {
          if (result.message === SUBMIT_GENERIC_ERROR) {
            setBanner({
              type: "error",
              heading: "Submit failed",
              message: (
                <>
                  We couldn&apos;t submit your product right now. Please try again.
                  {" "}
                  If this continues, contact
                  {" "}
                  <Link to={CONTACT_URL} className="underline">
                    support
                  </Link>
                  .
                </>
              ),
            });
            return;
          }
          setInlineError(result.message, result.field || FORM_ERROR_KEY);
          return;
        }
      }

      if (result.shouldSetWorkingProductId) {
        setWorkingProductId(result.resolvedProductId);
      }
      if (result.nextLoadedHyperlink) {
        loadedHyperlinkRef.current = result.nextLoadedHyperlink;
      }

      const navigationState = (result.hyperlinkUploadFailed || result.productUpdateFailed)
        ? {
          acrLinkUploadFailed: result.hyperlinkUploadFailed,
          productUpdateFailed: result.productUpdateFailed,
        }
        : null;
      navigateWithoutUnsavedPrompt(
        `/product-details/${result.resolvedProductId}`,
        navigationState
      );
    } finally {
      setIsSaving(false);
    }


  // eslint-disable-next-line react-hooks/exhaustive-deps -- productId/navigate; hook fns are stable
  }, [product, hyperlink, userEntity, workingProductId, submitProductWithTest, updateHyperlinkWithTest, submitFlowCreateHyperlinkForProduct, updatePublishedProductWithTest, navigateWithoutUnsavedPrompt, mapFieldFailures, setInlineError]);

  const onSaveDraftHandler = useCallback(async () => {
    setSubmitFieldErrors({});
    setBanner(null);
    setIsSaving(true);
    try {
      const result = await runSaveDraftProductFlow({
        workingProductId,
        product,
        hyperlink,
        loadedHyperlink: loadedHyperlinkRef.current,
        ownerPayload: currentOrg.ownerPayload,
        buildProductSavePayload,
        saveDraftProduct: saveDraftProductWithTest,
        updateProduct: updateProductForDraftWithTest,
        validateDraftHyperlink,
        hasAnyHyperlinkInput,
        buildHyperlinkPayloadForApi,
        hasHyperlinkChanges,
        updateHyperlink: updateHyperlinkForDraftWithTest,
        createHyperlinkForProduct: createHyperlinkForDraftWithTest,
        productNameRequiredMessage: PRODUCT_NAME_REQUIRED_MESSAGE,
      });

      if (!result.ok) {
        if (result.type === "validation_errors") {
          setSubmitFieldErrors(result.errors);
          focusFirstErrorField(result.errors);
          return;
        }
        if (result.type === "inline_error") {
          if (result.reason === "acr-link-upload-failed") {
            setBanner({
              type: "error",
              heading: "ACR link upload failed",
              message: "Your draft was saved, but the ACR link could not be uploaded. Please try again.",
            });
            return;
          }
          setInlineError(result.message, result.field || FORM_ERROR_KEY);
          return;
        }
      }

      if (result.shouldSetWorkingProductId) {
        setWorkingProductId(result.resolvedDraftId);
      }
      if (result.nextLoadedHyperlink) {
        loadedHyperlinkRef.current = result.nextLoadedHyperlink;
      }

      setBanner({ type: "success", message: "Your draft product has been successfully saved." });
      setHasUnsavedChanges(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsSaving(false);
    }
  }, [product, hyperlink, workingProductId, saveDraftProductWithTest, updateProductForDraftWithTest, updateHyperlinkForDraftWithTest, createHyperlinkForDraftWithTest, userEntity, focusFirstErrorField, setInlineError]);

  useEffect(() => {
    (async () => {
      // Each fetch below is wrapped independently so a single failure (e.g. a transient
      // network error) can't prevent the loading gate from ever clearing further down.
      try {
        const fetchAcrTypesResponse = await fetchAcrTypes();
        setAcrTypes(fetchAcrTypesResponse);
      } catch (acrTypesError) {
        setAcrTypes([]);
      }

      try {
        const fetchFedRampStatusesResponse = await fetchFedRampStatuses();
        setFedRampStatuses(fetchFedRampStatusesResponse);
      } catch (fedRampStatusesError) {
        setFedRampStatuses([]);
      }

      try {
        const categoriesResponse = await fetchCategories();
        setCategories(categoriesResponse);
      } catch (categoriesError) {
        setCategories([]);
      }

      try {
        const agencyResponse = await axios.get(`${REACT_APP_ACR_API_PROTOCOL}://${REACT_APP_ACR_API_BASE_URL}/api/agency/all`);
        const agencyData = agencyResponse?.data;
        const agencyList = Array.isArray(agencyData) ? agencyData : agencyData?.data;
        setAgencies((Array.isArray(agencyList) ? agencyList : []).filter((agency) => agency?.id && agency?.name));
        setAgencyLoadError(null);
      } catch (agencyError) {
        setAgencies([]);
        setAgencyLoadError("Agency visibility options are currently unavailable. You can still submit this product.");
      }

      if (productId && userEntity) {
        isHydratingRef.current = true;
        setIsLoadingProduct(true);
        try {
          const productResponse = await getProduct(productId);
          const productVendorId = String(productResponse?.vendorId || productResponse?.vendor?.id || "");
          const productAgencyId = String(productResponse?.agencyId || "");
          const userOrgId = String(userEntity?.entity?.id || "");
          // Compare the product's owner id against the user's org, using whichever owner type
          // the user belongs to (vendors own via vendorId, agencies via agencyId).
          const productOwnerId = userEntity?.type === ENTITY_TYPE_AGENCY ? productAgencyId : productVendorId;
          if (
            (userEntity?.type === ENTITY_TYPE_VENDOR || userEntity?.type === ENTITY_TYPE_AGENCY) &&
            productOwnerId &&
            userOrgId &&
            productOwnerId !== userOrgId
          ) {
            setInlineError("You can only submit or edit products for your own organization.", FORM_ERROR_KEY);
            setProduct(normalizeProductForForm(null));
            return;
          }
          const base = normalizeProductForForm(productResponse);
          const loadedVisibility = base.visibility || PRODUCT_VISIBILITY_PUBLIC;
          const loadedHiddenIds = getHiddenAgencyIds(productResponse);
          setProduct({
            ...base,
            hiddenAgencyIds:
              loadedVisibility === PRODUCT_VISIBILITY_BUYERS_ONLY
                ? [...new Set(loadedHiddenIds.map(String).filter(Boolean))]
                : [],
          });

          let prefill = createEmptyHyperlinkState();
          let mappedHyperlink = null;

          const productHyperlinks = Array.isArray(productResponse?.hyperlinks)
            ? productResponse.hyperlinks.filter((item) => item?.isActive !== false)
            : [];
          if (productHyperlinks.length > 0) {
            mappedHyperlink = mapHyperlinkFromApiToForm(productHyperlinks[0]);
          }

          if (!mappedHyperlink) {
            const hyperlinkReferenceIds = Array.isArray(productResponse?.hyperlinkReferenceIds)
              ? productResponse.hyperlinkReferenceIds.filter(Boolean)
              : [];
            if (hyperlinkReferenceIds.length > 0) {
              const hyperlinkResults = await Promise.allSettled(
                hyperlinkReferenceIds.map((id) => getHyperlinkById(id))
              );
              const activeReferencedHyperlinks = hyperlinkResults
                .filter((result) => result.status === "fulfilled")
                .map((result) => result.value)
                .filter((item) => item?.isActive !== false);
              if (activeReferencedHyperlinks.length > 0) {
                mappedHyperlink = mapHyperlinkFromApiToForm(activeReferencedHyperlinks[0]);
              }
            }
          }

          if (!mappedHyperlink) {
            const hyperlinkResponse = await getHyperlinkByProductId(productId);
            if (hyperlinkResponse && hyperlinkResponse.isActive !== false) {
              mappedHyperlink = mapHyperlinkFromApiToForm(hyperlinkResponse);
            }
          }

          if (mappedHyperlink) {
            setHyperlink(mappedHyperlink);
            loadedHyperlinkRef.current = mappedHyperlink;
          } else {
            if (productResponse?.isPublished === true) {
              const reports = await fetchReportsByProduct(productId);
              const report = Array.isArray(reports)
                ? reports.find((r) => r?.completionStatus === "Completed" || r?.completionStatus === "complete")
                : null;
              if (report) {
                const details = report.reportDetails || report.ReportDetails || {};
                const repositoryUrl = details.repositoryUrl || details.RepositoryUrl;
                if (repositoryUrl) {
                  prefill = { ...prefill, url: String(repositoryUrl).trim() };
                }
                const vInAcr = report.version || details.version || details.Version;
                if (vInAcr) {
                  prefill = { ...prefill, version: String(vInAcr) };
                }
                const when = inSiteAcrAddedOn(report);
                const d = safeFormatDateForInput(when);
                if (d) {
                  prefill = { ...prefill, authoredDate: d };
                }
              }
            }
            setHyperlink(prefill);
            loadedHyperlinkRef.current = prefill;
          }

          setSubmitFieldErrors({});
          setHasUnsavedChanges(false);
        } finally {
          isHydratingRef.current = false;
          setIsLoadingProduct(false);
        }
      } else if (!productId) {
        setProduct(normalizeProductForForm(null));
        const emptyHyperlink = createEmptyHyperlinkState();
        setHyperlink(emptyHyperlink);
        loadedHyperlinkRef.current = emptyHyperlink;
        setSubmitFieldErrors({});
        setHasUnsavedChanges(false);
        setIsLoadingProduct(false);
      }
    })();
  }, [userEntity, productId, getProduct, getHyperlinkByProductId, getHyperlinkById, fetchCategories, fetchAcrTypes, fetchFedRampStatuses, fetchReportsByProduct, setInlineError]);

  useEffect(() => {
    if (!hasInitializedDirtyTrackingRef.current) {
      hasInitializedDirtyTrackingRef.current = true;
      return;
    }
    if (!isHydratingRef.current) {
      setSkipUnsavedPrompt(false);
      setHasUnsavedChanges(true);
    }
  }, [product, hyperlink]);

  useEffect(() => {
    if (blocker.state === "unblocked") {
      isHandlingBlockerRef.current = false;
      return;
    }

    if (blocker.state !== "blocked" || isHandlingBlockerRef.current) {
      return;
    }

    isHandlingBlockerRef.current = true;
    // eslint-disable-next-line no-alert -- required by acceptance criteria for navigation guard prompt
    if (window.confirm(unsavedChangesPrompt)) {
      setSkipUnsavedPrompt(true);
      setHasUnsavedChanges(false);
      blocker.proceed();
      return;
    }
    setSkipUnsavedPrompt(false);
    blocker.reset();
    isHandlingBlockerRef.current = false;
  }, [blocker, unsavedChangesPrompt]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedChanges) {
        return;
      }

      event.preventDefault();
      // eslint-disable-next-line no-param-reassign -- required by browser beforeunload API
      event.returnValue = unsavedChangesPrompt;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, unsavedChangesPrompt]);

  useEffect(() => {
    if (!banner || !bannerRef.current) {
      return;
    }
    if (banner.kind === "validation-summary" && summaryHeadingRef.current) {
      summaryHeadingRef.current.focus();
      return;
    }
    bannerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [banner]);

  useEffect(() => {
    if (banner?.kind !== "validation-summary") {
      return;
    }
    if (getValidationSummaryErrors(submitFieldErrors).length === 0) {
      setBanner(null);
    }
  }, [banner, submitFieldErrors]);

  if (!isLoggedIn()) {
    return (
      <AccessStatus access={ACCESS_NO_LOGIN} />
    )
  }

  if (!checkEntityAccessLevel([ACCESS_LEVEL_ADMIN, ACCESS_LEVEL_USER])) {
    return (
      <AccessStatus access={ACCESS_DENIED} />
    )
  }

  if (userEntity?.type !== ENTITY_TYPE_VENDOR && userEntity?.type !== ENTITY_TYPE_AGENCY) {
    return (
      <AccessStatus access={ACCESS_DENIED} />
    );
  }

  const validationSummaryErrors = getValidationSummaryErrors(submitFieldErrors);

  return (
    <SectionContainer>
      <Breadcrumb links={[{"href": HOME_URL, "text": "Home"}, {"href": PRODUCT_SUBMIT_URL, "text": "Product Submit"}]} />
      <h1 className="text-primary-light text-3xl mb-2">
        {productId ? "Edit" : "Submit New"} Product for {userEntity?.entity?.name}
      </h1>
      {banner ? (
        <div ref={bannerRef} className="mb-4 w-full max-w-4xl">
          {banner.kind === "validation-summary" ? (
            <Alert type="error" onClose={() => setBanner(null)}>
              <h2 className="usa-alert__heading" tabIndex={-1} ref={summaryHeadingRef}>
                {VALIDATION_SUMMARY_HEADING}
              </h2>
              {validationSummaryErrors.length ? (
                <ul className="usa-list">
                  {validationSummaryErrors.map(({ field, id, message }) => (
                    <li key={field}>
                      <a
                        href={`#${id}`}
                        onClick={(event) => {
                          event.preventDefault();
                          focusFieldForError(field);
                        }}
                      >
                        {message}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
            </Alert>
          ) : (
            <Alert
              heading={banner.heading || (banner.type === "error" ? "Error" : "Success")}
              type={banner.type || "success"}
              onClose={() => setBanner(null)}
            >
              {banner.message}
            </Alert>
          )}
        </div>
      ) : null}
      <div className="mb-8">
        Please fill out the form below to create a new listing for your product. The information you provide will appear on the product overview page, as well as the product listing table. It is important to include as much detail as possible to help buyers make an informed decision about your product.
      </div>
      {isLoadingProduct ? (
        <div className="my-8 flex items-center gap-2" role="status" aria-live="polite">
          <IndeterminateSpinner type="circle" color="blue" />
          <span className="usa-sr-only">Loading product details...</span>
        </div>
      ) : (
      <Form id="productForm" onSubmit={onSubmitHandler} className='max-w-full'>
        <ProductInformationSection
          product={product}
          categories={categories}
          pscCodes={pscCodes}
          fedRampStatuses={fedRampStatuses}
          clearFieldErrors={clearFieldErrors}
          setProduct={setProduct}
          getFieldError={getFieldError}
          renderInlineFieldError={renderInlineFieldError}
          FormFieldRequired={FormFieldRequired}
          onNameBlur={handleProductNameBlur}
        />
        <ProductVisibilitySection
          normalizedProductVisibility={normalizedProductVisibility}
          clearFieldErrors={clearFieldErrors}
          setProduct={setProduct}
          getFieldError={getFieldError}
          renderInlineFieldError={renderInlineFieldError}
          FormFieldRequired={FormFieldRequired}
          agencyExclusionsApply={agencyExclusionsApply}
          selectedHiddenAgencies={selectedHiddenAgencies}
          product={product}
          agencies={agencies}
          hiddenAgencySelectionAnnouncement={hiddenAgencySelectionAnnouncement}
          agencyLoadError={agencyLoadError}
        />
        <AcrInformationSection
          hyperlink={hyperlink}
          acrTypes={acrTypes}
          clearFieldErrors={clearFieldErrors}
          setHyperlinkName={setHyperlinkName}
          setHyperlink={setHyperlink}
          normalizeHyperlinkUrlForSave={normalizeHyperlinkUrlForSave}
          getFieldError={getFieldError}
          renderInlineFieldError={renderInlineFieldError}
          FormFieldRequired={FormFieldRequired}
          REQUEST_LINK_MESSAGE={REQUEST_LINK_MESSAGE}
        />
        <ProductPocSection
          productPoc={normalizeProductPocForForm(product.productPoc)}
          setProductPoc={(nextProductPoc) => {
            setProduct((prev) => ({
              ...prev,
              productPoc: normalizeProductPocForForm(nextProductPoc),
            }));
          }}
          clearFieldErrors={clearFieldErrors}
          getFieldError={getFieldError}
          renderInlineFieldError={renderInlineFieldError}
          FormFieldRequired={FormFieldRequired}
          stateLabelValues={stateLabelValues}
        />
        <section>
          {renderInlineFieldError(getFieldError(FORM_ERROR_KEY))}
          <ButtonGroup>
            <ButtonGroupItem>
              <Button type="button" outline onClick={() => {
                // eslint-disable-next-line no-alert -- required by acceptance criteria for navigation guard prompt
                if (hasUnsavedChanges && !window.confirm(unsavedChangesPrompt)) {
                  return;
                }
                navigateWithoutUnsavedPrompt(PRODUCT_LIST_SUBMIT_URL);
              }}>Cancel</Button>
            </ButtonGroupItem>
            {canSaveDraft ? (
              <ButtonGroupItem>
                <Button
                  type="button"
                  disabled={isSaving}
                  outline
                  onClick={onSaveDraftHandler}
                >
                  Save Draft
                </Button>
              </ButtonGroupItem>
            ) : null}
            <ButtonGroupItem>
              <Button type="button" disabled={isSaving} onClick={onSubmitHandler}>Submit</Button>
            </ButtonGroupItem>
          </ButtonGroup>
        </section>
      </Form>
      )}
    </SectionContainer>
  )
}
